<template>
  <el-dialog
    title="选择指定商品"
    :visible.sync="visible"
    width="88%"
    append-to-body
    :close-on-click-modal="false"
    @open="load"
  >
    <div class="selector-tip">
      <i class="el-icon-info" />勾选商品后，优惠券适用于该商品的全部
      SKU；点击商品行左侧箭头可查看具体 SKU。
    </div>
    <div class="filter-container">
      <el-input
        v-model.trim="query.keyword"
        clearable
        placeholder="商品名称 / 商品ID"
        style="width:260px"
        @keyup.enter.native="search"
      />
      <el-button type="primary" icon="el-icon-search" @click="search">搜索</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

    <el-table
      ref="table"
      v-loading="loading"
      :data="list"
      row-key="id"
      border
      @selection-change="selectPage"
      @expand-change="handleExpandChange"
    >
      <el-table-column type="expand" width="48">
        <template slot-scope="props">
          <div class="sku-panel">
            <div class="sku-panel__title">
              <span>商品 SKU</span
              ><span class="sku-panel__count"
                >共 {{ props.row.skus ? props.row.skus.length : 0 }} 个</span
              >
            </div>
            <el-table
              v-loading="!!specLoading[props.row.id]"
              :data="props.row.skus || []"
              size="small"
              border
              empty-text="暂无 SKU 数据"
            >
              <el-table-column prop="id" label="SKU ID" width="110" align="center" />
              <el-table-column prop="combNames" label="规格" min-width="220">
                <template slot-scope="scope">{{
                  scope.row.combNames || scope.row.name || "默认规格"
                }}</template>
              </el-table-column>
              <el-table-column prop="price" label="销售价" width="120" align="right">
                <template slot-scope="scope">¥ {{ formatMoney(scope.row.price) }}</template>
              </el-table-column>
              <el-table-column prop="supplyPrice" label="供货价" width="120" align="right">
                <template slot-scope="scope">¥ {{ formatMoney(scope.row.supplyPrice) }}</template>
              </el-table-column>
              <el-table-column prop="stock" label="库存" width="100" align="center" />
              <el-table-column prop="combTypes" label="规格类型" min-width="160" />
            </el-table>
          </div>
        </template>
      </el-table-column>
      <el-table-column type="selection" width="48" :reserve-selection="true" />
      <el-table-column prop="id" label="商品ID" width="100" align="center" />
      <el-table-column label="商品信息" min-width="320">
        <template slot-scope="scope">
          <div class="goods-cell">
            <el-image :src="scope.row.mainGraph" fit="cover"
              ><div slot="error" class="image-placeholder">
                <i class="el-icon-picture-outline" /></div
            ></el-image>
            <div class="goods-cell__content">
              <span class="goods-cell__name">{{ scope.row.name }}</span
              ><span class="goods-cell__hint">展开查看 SKU</span>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="销售价" width="150" align="right"
        ><template slot-scope="scope">¥ {{ priceRange(scope.row) }}</template></el-table-column
      >
      <el-table-column prop="stock" label="库存" width="100" align="center" />
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="scope"
          ><el-tag :type="Number(scope.row.status) === 2 ? 'success' : 'info'" size="small">{{
            Number(scope.row.status) === 2 ? "已上架" : "未上架"
          }}</el-tag></template
        >
      </el-table-column>
    </el-table>

    <el-pagination
      background
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      :current-page="query.page"
      :page-size="query.pageSize"
      :page-sizes="[10, 20, 30, 50]"
      @current-change="changePage"
      @size-change="changeSize"
    />
    <span slot="footer"
      ><span class="selected-count">已选择 {{ selected.length }} 个商品</span>
      <el-button @click="visible = false">取消</el-button
      ><el-button type="primary" @click="confirm">确定</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getGoodsList, getGoodsEditDetail } from "@/api/product";

export default {
  name: "CouponProductSelectDialog",
  props: { value: Boolean, selectedRows: { type: Array, default: () => [] } },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      selected: [],
      specLoading: {},
      query: { page: 1, pageSize: 10, keyword: "" }
    };
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      }
    }
  },
  watch: {
    selectedRows: {
      immediate: true,
      deep: true,
      handler(v) {
        this.selected = (v || []).slice();
      }
    }
  },
  methods: {
    async load() {
      this.selected = (this.selectedRows || []).slice();
      await this.fetch();
      this.restore();
    },
    async fetch() {
      this.loading = true;
      try {
        const r = await getGoodsList({ ...this.query, deleted: 0, status: 2 });
        const d = r.data || {};
        this.list = (d.entitys || []).map(item => ({ ...item, skus: null }));
        this.total = d.totalCount || 0;
      } finally {
        this.loading = false;
      }
    },
    restore() {
      this.$nextTick(() => {
        if (!this.$refs.table) return;
        (this.list || []).forEach(row =>
          this.$refs.table.toggleRowSelection(
            row,
            this.selected.some(v => String(v.id) === String(row.id))
          )
        );
      });
    },
    selectPage(rows) {
      const pageIds = this.list.map(v => String(v.id));
      this.selected = this.selected.filter(v => !pageIds.includes(String(v.id))).concat(rows);
    },
    async handleExpandChange(row, expandedRows) {
      const expanded = Array.isArray(expandedRows)
        ? expandedRows.some(item => String(item.id) === String(row.id))
        : !!expandedRows;
      if (!expanded || Array.isArray(row.skus)) return;
      this.$set(this.specLoading, row.id, true);
      try {
        const response = await getGoodsEditDetail({ id: row.id });
        if (Number(response.bizcode) === 100) {
          this.$set(row, "skus", response.data && response.data.combs ? response.data.combs : []);
        } else {
          this.$set(row, "skus", []);
          this.$message.error(response.msg || "获取 SKU 信息失败");
        }
      } catch (error) {
        this.$set(row, "skus", []);
        this.$message.error("获取 SKU 信息失败");
      } finally {
        this.$set(this.specLoading, row.id, false);
      }
    },
    search() {
      this.query.page = 1;
      this.fetch().then(this.restore);
    },
    resetSearch() {
      this.query.keyword = "";
      this.query.page = 1;
      this.fetch().then(this.restore);
    },
    changePage(v) {
      this.query.page = v;
      this.fetch().then(this.restore);
    },
    changeSize(v) {
      this.query.pageSize = v;
      this.query.page = 1;
      this.fetch().then(this.restore);
    },
    formatMoney(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number.toFixed(2) : "--";
    },
    priceRange(row) {
      const min = this.formatMoney(row.minPrice);
      const max = this.formatMoney(row.maxPrice);
      return max !== "--" && max !== min ? `${min} ~ ${max}` : min;
    },
    confirm() {
      if (!this.selected.length) {
        this.$message.warning("请至少选择一个商品");
        return;
      }
      // scopeValue 保存商品 ID；展开的 SKU 只用于查看，不能传 SKU ID。
      this.$emit("confirm", this.selected.slice());
      this.visible = false;
    }
  }
};
</script>

<style scoped>
.selector-tip {
  padding: 10px 14px;
  margin-bottom: 16px;
  color: #5b3cc4;
  background: #f5f2ff;
  border: 1px solid #e6deff;
  border-radius: 4px;
}
.selector-tip i {
  margin-right: 6px;
}
.filter-container {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}
.goods-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.goods-cell .el-image {
  flex: 0 0 54px;
  width: 54px;
  height: 54px;
  border-radius: 4px;
  background: #f5f7fa;
}
.image-placeholder {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
}
.goods-cell__content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.goods-cell__name {
  overflow: hidden;
  color: #303133;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.goods-cell__hint {
  margin-top: 5px;
  color: #909399;
  font-size: 12px;
}
.sku-panel {
  padding: 12px 18px 18px 70px;
  background: #fafafa;
}
.sku-panel__title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  color: #303133;
  font-weight: 600;
}
.sku-panel__count {
  color: #909399;
  font-size: 12px;
  font-weight: 400;
}
.el-pagination {
  margin-top: 16px;
  text-align: right;
}
.selected-count {
  float: left;
  color: #606266;
  line-height: 36px;
}
</style>
